import type { OrderAttachment, OrderComment } from '../types';
import { downloadAttachment } from '../lib/download';
import { dateTime } from '../lib/format';

/**
 * Лента переписки по заказу. Общая для кабинета художника и кабинета заказчика:
 * сообщения в них одни и те же, различаются только подписи авторов и то, что
 * приватные заметки видит лишь художник.
 */
export function CommentThread({
  comments,
  emptyText,
  counterpartName,
}: {
  comments: OrderComment[];
  emptyText: string;
  /** Имя собеседника — им подписаны чужие сообщения без имени автора. */
  counterpartName: string;
}) {
  if (comments.length === 0) {
    return <p className="muted">{emptyText}</p>;
  }

  return (
    <div className="stack stack--compact">
      {comments.map((comment) => (
        <div
          key={comment.id}
          className={`bubble ${comment.own ? 'bubble--own' : ''} ${
            comment.visibility === 'PRIVATE' ? 'bubble--private' : ''
          }`}
        >
          <div className="card-row">
            <strong>
              {comment.own ? 'Вы' : (comment.author.firstName ?? counterpartName)}
              {comment.kind === 'REVISION' && ' · правки'}
              {comment.visibility === 'PRIVATE' && ' · заметка'}
            </strong>
            <span className="meta">{dateTime(comment.createdAt)}</span>
          </div>
          {comment.body && <p>{comment.body}</p>}
          <CommentAttachments attachments={comment.attachments} />
        </div>
      ))}
    </div>
  );
}

/**
 * Картинки из переписки: их присылают фотографией в чат бота, а сюда они
 * попадают ссылкой на `/v1/images`. Вложение без ссылки лежит только в
 * Telegram — показывать в приложении нечего.
 */
export function CommentAttachments({ attachments }: { attachments: OrderAttachment[] }) {
  const visible = attachments.filter((attachment) => attachment.url !== null);
  const images = visible.filter((attachment) => attachment.mimeType.startsWith('image/'));
  // Документ показывать нечем: у PDF и ZIP нет превью, только строка со ссылкой.
  const documents = visible.filter((attachment) => !attachment.mimeType.startsWith('image/'));

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="bubble__attachments">
      {images.map((attachment) => (
        <a key={attachment.id} href={attachment.url ?? undefined} target="_blank" rel="noreferrer">
          <img src={attachment.url ?? undefined} alt={attachment.fileName} loading="lazy" />
        </a>
      ))}
      {documents.map((attachment) => (
        <button
          key={attachment.id}
          type="button"
          className="bubble__document"
          onClick={() => void downloadAttachment(attachment.downloadUrl!, attachment.fileName)}
        >
          {attachment.fileName}
        </button>
      ))}
    </div>
  );
}
